import React, { useContext, useEffect, useState } from "react";
import {
  Box,
  Heading,
  Text,
  Spinner,
  VStack,
  Button,
  Container,
  Skeleton,
  useColorModeValue,
  Flex,
  Editable,
  EditableInput,
  FormControl,
  FormLabel,
  EditablePreview,
  Input,
  useToast,
  Link,
  InputGroup,
  InputRightElement,
  Select,
  Checkbox,
  FormHelperText,
  Collapse,
  useDisclosure,
} from "@chakra-ui/react";
import { EditIcon } from "@chakra-ui/icons";
import { useNavigate } from "react-router-dom";
import Navbar from "../../components/Navbar";
import AuthContext from "../../context/AuthContext";
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import useAxios from "../../utils/useAxios";

interface User {
  first_name: string;
  last_name: string;
}

interface Organization {
  id: number;
  name: string;
}

const InviteUserForm = () => {
  const { isOpen, onToggle } = useDisclosure();
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const api = useAxios();
  const toast = useToast();
  const bg = useColorModeValue("white", "gray.900");

  const handleInvite = async () => {
    if (!firstName || !lastName || !email) {
      toast({
        title: "Error",
        description: "Please fill in all fields.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setLoading(true);
    try {
      await api.post("users/organization/invite-user/", {
        first_name: firstName,
        last_name: lastName,
        email: email,
      });
      toast({
        title: "Success",
        description: "User invited successfully.",
        status: "success",
        duration: 5000,
        isClosable: true,
      });
      setFirstName("");
      setLastName("");
      setEmail("");
      onToggle();
    } catch (error: any) {
      let description = "Failed to invite user.";
      if (error.response?.data?.detail) {
        // If the detail is an array (e.g. from DRF validation), take the first one
        description = Array.isArray(error.response.data.detail)
          ? error.response.data.detail[0]
          : error.response.data.detail;
      } else if (error.response?.data?.email) {
        description = error.response.data.email[0];
      } else if (error.response?.data?.organization) {
        description = error.response.data.organization[0];
      } else if (typeof error.response?.data === "string") {
        description = error.response.data;
      }

      toast({
        title: "Error",
        description: description,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container p={4} bg={bg} boxShadow="lg" borderRadius="md" mt={4} maxW="xl" mb={4}>
      <Text fontWeight="bold" fontSize="lg" mb={2}>
        <Trans>Invite User</Trans>
      </Text>
      <Text fontSize="sm" mb={4} color={useColorModeValue("gray.600", "gray.300")}>
        <Trans>Invite a new user to your organization.</Trans>
      </Text>

      <Button
        fontSize="md"
        onClick={onToggle}
        variant={isOpen ? "solid" : "outline"}
        size="sm"
        colorScheme={isOpen ? "gray" : "dark"}
        borderRadius={"unset"}
        p={2}
        flexShrink={0}
        fontStyle="semibold"
        mb={isOpen ? 4 : 0}
        _hover={{ bg: "blackAlpha.100" }}
      >
        <Trans>{isOpen ? "Cancel" : "Invite User"}</Trans>
      </Button>

      <Collapse in={isOpen} animateOpacity>
        <VStack spacing={4} align="stretch">
          <FormControl id="firstName" isRequired>
            <FormLabel>
              <Trans>First Name</Trans>
            </FormLabel>
            <Input
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              placeholder={t`John`}
            />
          </FormControl>
          <FormControl id="lastName" isRequired>
            <FormLabel>
              <Trans>Last Name</Trans>
            </FormLabel>
            <Input
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              placeholder={t`Doe`}
            />
          </FormControl>
          <FormControl id="email" isRequired>
            <FormLabel>
              <Trans>Email Address</Trans>
            </FormLabel>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t`john.doe@example.com`}
            />
          </FormControl>
          <Button colorScheme="blue" onClick={handleInvite} isLoading={loading} width="full">
            <Trans>Send Invitation</Trans>
          </Button>
        </VStack>
      </Collapse>
    </Container>
  );
};

const ProfileView: React.FC = () => {
  const { user, logoutUser, userSettings } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const [userData, setUserData] = useState<User>();
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [whatsappPhone, setWhatsappPhone] = useState("");
  const [whatsappCountryCode, setWhatsappCountryCode] = useState("47"); // Default to Norway
  const [whatsappOptIn, setWhatsappOptIn] = useState(false);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [selectedOrganizationId, setSelectedOrganizationId] = useState<number | string>("");
  const api = useAxios();
  const toast = useToast();

  const [fieldStatus, setFieldStatus] = useState<boolean>(false);

  const _date_joined = new Date(user.date_joined);
  const isAdmin = userSettings?.user_type === "ADMIN";
  const inputBgColor = useColorModeValue("gray.50", "gray.700");

  useEffect(() => {
    if (!user) {
      navigate("/login");
    } else {
      setFirstName(user.first_name);
      setLastName(user.last_name);

      // Parse WhatsApp phone number - separate country code from number
      if (user.whatsapp_phone) {
        // Common country codes and their lengths
        const countryCodes = [
          "1",
          "7",
          "20",
          "27",
          "30",
          "31",
          "32",
          "33",
          "34",
          "36",
          "39",
          "40",
          "41",
          "43",
          "44",
          "45",
          "46",
          "47",
          "48",
          "49",
          "51",
          "52",
          "53",
          "54",
          "55",
          "56",
          "57",
          "58",
          "60",
          "61",
          "62",
          "63",
          "64",
          "65",
          "66",
          "81",
          "82",
          "84",
          "86",
          "90",
          "91",
          "92",
          "93",
          "94",
          "95",
          "98",
          "212",
          "213",
          "216",
          "218",
          "220",
          "221",
          "222",
          "223",
          "224",
          "225",
          "226",
          "227",
          "228",
          "229",
          "230",
          "231",
          "232",
          "233",
          "234",
          "235",
          "236",
          "237",
          "238",
          "239",
          "240",
          "241",
          "242",
          "243",
          "244",
          "245",
          "246",
          "248",
          "249",
          "250",
          "251",
          "252",
          "253",
          "254",
          "255",
          "256",
          "257",
          "258",
          "260",
          "261",
          "262",
          "263",
          "264",
          "265",
          "266",
          "267",
          "268",
          "269",
          "290",
          "291",
          "297",
          "298",
          "299",
          "350",
          "351",
          "352",
          "353",
          "354",
          "355",
          "356",
          "357",
          "358",
          "359",
          "370",
          "371",
          "372",
          "373",
          "374",
          "375",
          "376",
          "377",
          "378",
          "380",
          "381",
          "382",
          "383",
          "385",
          "386",
          "387",
          "389",
          "420",
          "421",
          "423",
          "500",
          "501",
          "502",
          "503",
          "504",
          "505",
          "506",
          "507",
          "508",
          "509",
          "590",
          "591",
          "592",
          "593",
          "594",
          "595",
          "596",
          "597",
          "598",
          "599",
          "670",
          "672",
          "673",
          "674",
          "675",
          "676",
          "677",
          "678",
          "679",
          "680",
          "681",
          "682",
          "683",
          "685",
          "686",
          "687",
          "688",
          "689",
          "690",
          "691",
          "692",
          "850",
          "852",
          "853",
          "855",
          "856",
          "870",
          "880",
          "886",
          "960",
          "961",
          "962",
          "963",
          "964",
          "965",
          "966",
          "967",
          "968",
          "970",
          "971",
          "972",
          "973",
          "974",
          "975",
          "976",
          "977",
          "992",
          "993",
          "994",
          "995",
          "996",
          "998",
        ];

        // Try to match country code (sorted by length, longest first)
        const sortedCodes = countryCodes.sort((a, b) => b.length - a.length);
        let matchedCode = "47"; // default
        let phoneNumber = user.whatsapp_phone;

        for (const code of sortedCodes) {
          if (user.whatsapp_phone.startsWith(code)) {
            matchedCode = code;
            phoneNumber = user.whatsapp_phone.substring(code.length);
            break;
          }
        }

        setWhatsappCountryCode(matchedCode);
        setWhatsappPhone(phoneNumber);
      }

      setWhatsappOptIn(user.whatsapp_opt_in || false);
      // Only set organization for non-admin users, admin users get it set after orgs load
      if (!isAdmin && user.organization) {
        setSelectedOrganizationId(user.organization);
      }
      setLoading(false);
    }
  }, [user, isAdmin]);

  // Fetch organizations for admin users
  useEffect(() => {
    const fetchOrganizations = async () => {
      if (isAdmin) {
        try {
          const response = await api.get("users/organizations/");
          let orgList = response.data;

          // Ensure current user's organization is in the list
          if (user.organization && user.organization_name) {
            const currentOrgExists = orgList.some(
              (org: Organization) => org.id === user.organization,
            );
            if (!currentOrgExists) {
              orgList = [{ id: user.organization, name: user.organization_name }, ...orgList];
            }
          }

          setOrganizations(orgList);

          // Set the selected organization after organizations are loaded
          if (user.organization) {
            setSelectedOrganizationId(user.organization);
          } else {
            // Try to find organization by name if ID is not available
            if (user.organization_name && orgList.length > 0) {
              const foundOrg = orgList.find(
                (org: Organization) => org.name === user.organization_name,
              );
              if (foundOrg) {
                setSelectedOrganizationId(foundOrg.id);
              }
            }
          }
        } catch (error) {
          console.error("Failed to fetch organizations:", error);
          toast({
            title: "Error",
            description: "Failed to fetch organizations.",
            status: "error",
            duration: 3000,
            isClosable: true,
          });
        }
      }
    };

    if (isAdmin) {
      fetchOrganizations();
    }
  }, [isAdmin, user.organization, user.organization_name]);

  const changeUserData = async (updatedData: {
    first_name: string;
    last_name: string;
    whatsapp_phone?: string;
    whatsapp_opt_in?: boolean;
  }) => {
    setLoading(true);
    try {
      const response = await api.put("users/profile-preferences/", updatedData);
      toast({
        title: "Success",
        description: "User data updated successfully.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      setUserData(response.data);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update user data.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const changeUserOrganization = async (organizationId: number) => {
    setLoading(true);
    try {
      await api.post("users/admin/change-user-organization/", {
        organization_id: organizationId,
      });

      toast({
        title: "Success",
        description: "Organization updated successfully.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      // Refresh the page to get updated user data
      window.location.reload();
    } catch (error) {
      console.error("Error changing organization:", error);
      toast({
        title: "Error",
        description: "Failed to update organization.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Navbar>
      <Skeleton isLoaded={!loading}>
        <Container
          p={4}
          bg={useColorModeValue("white", "gray.900")}
          boxShadow="lg"
          borderRadius="md"
          mt={4}
          maxW="xl"
        >
          <Heading as="h1" size="2xl" textAlign="center" mb={4}>
            <Trans>My Profile</Trans>
          </Heading>

          <VStack spacing={6} align="start">
            <Box>
              <FormControl>
                <FormLabel fontWeight="bold">
                  <Trans>First Name:</Trans>
                </FormLabel>
                <Input
                  fontSize="xl"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder={user.first_name || "-"}
                  isDisabled={!fieldStatus}
                />
              </FormControl>
            </Box>

            <Box>
              <FormControl>
                <FormLabel fontWeight="bold">
                  <Trans>Last Name:</Trans>
                </FormLabel>
                <Input
                  fontSize="xl"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder={user.last_name || "-"}
                  isDisabled={!fieldStatus}
                />
              </FormControl>
            </Box>

            <Box>
              <Text fontWeight="bold">Email:</Text>
              <Text>{user.email || "-"}</Text>
            </Box>

            <Box width="100%">
              <FormControl>
                <FormLabel fontWeight="bold">
                  <Trans>Phone Number:</Trans>
                </FormLabel>
                <Flex gap={2}>
                  <Select
                    width="120px"
                    value={whatsappCountryCode}
                    onChange={(e) => setWhatsappCountryCode(e.target.value)}
                    isDisabled={!fieldStatus}
                  >
                    <option value="1">🇺🇸 +1</option>
                    <option value="7">🇷🇺 +7</option>
                    <option value="20">🇪🇬 +20</option>
                    <option value="27">🇿🇦 +27</option>
                    <option value="30">🇬🇷 +30</option>
                    <option value="31">🇳🇱 +31</option>
                    <option value="32">🇧🇪 +32</option>
                    <option value="33">🇫🇷 +33</option>
                    <option value="34">🇪🇸 +34</option>
                    <option value="39">🇮🇹 +39</option>
                    <option value="40">🇷🇴 +40</option>
                    <option value="41">🇨🇭 +41</option>
                    <option value="43">🇦🇹 +43</option>
                    <option value="44">🇬🇧 +44</option>
                    <option value="45">🇩🇰 +45</option>
                    <option value="46">🇸🇪 +46</option>
                    <option value="47">🇳🇴 +47</option>
                    <option value="48">🇵🇱 +48</option>
                    <option value="49">🇩🇪 +49</option>
                    <option value="61">🇦🇺 +61</option>
                    <option value="81">🇯🇵 +81</option>
                    <option value="86">🇨🇳 +86</option>
                    <option value="91">🇮🇳 +91</option>
                  </Select>
                  <Input
                    flex="1"
                    fontSize="xl"
                    type="tel"
                    value={whatsappPhone}
                    onChange={(e) => {
                      // Remove any non-digit characters
                      const cleaned = e.target.value.replace(/\D/g, "");
                      setWhatsappPhone(cleaned);
                    }}
                    placeholder={t`e.g. 90123456`}
                    isDisabled={!fieldStatus}
                  />
                </Flex>
                <FormHelperText>
                  <Trans>
                    Your number: +{whatsappCountryCode}
                    {whatsappPhone || "xxxxxxxx"}
                  </Trans>
                </FormHelperText>
                {user.whatsapp_verified && (
                  <Text fontSize="sm" color="green.500" mt={1}>
                    ✓ Verified
                  </Text>
                )}
              </FormControl>

              <FormControl mt={3}>
                <Checkbox
                  isChecked={whatsappOptIn}
                  onChange={(e) => setWhatsappOptIn(e.target.checked)}
                  isDisabled={!fieldStatus}
                >
                  <Trans>I want to receive messages on WhatsApp (Coming soon)</Trans>
                </Checkbox>
              </FormControl>
            </Box>

            <Box>
              {isAdmin ? (
                <FormControl>
                  <FormLabel fontWeight="bold">
                    <Trans>Organization:</Trans>
                  </FormLabel>
                  <Select
                    fontSize="xl"
                    value={selectedOrganizationId ? String(selectedOrganizationId) : ""}
                    onChange={(e) => setSelectedOrganizationId(Number(e.target.value) || "")}
                    placeholder={t`Select organization`}
                    isDisabled={!fieldStatus}
                  >
                    {organizations.map((org) => (
                      <option key={org.id} value={org.id}>
                        {org.name}
                      </option>
                    ))}
                  </Select>
                </FormControl>
              ) : (
                <FormControl>
                  <FormLabel fontWeight="bold">
                    <Trans>Organization:</Trans>
                  </FormLabel>
                  <Input
                    fontSize="xl"
                    value={user.organization_name || ""}
                    isReadOnly
                    isDisabled
                    bg={inputBgColor}
                  />
                </FormControl>
              )}
            </Box>

            <Flex alignItems="center" width="100%">
              {!fieldStatus ? (
                <Button
                  colorScheme="gray"
                  variant="outline"
                  borderRadius={"unset"}
                  p={2}
                  flex="1"
                  onClick={() => setFieldStatus(true)}
                  rightIcon={<EditIcon />}
                >
                  Edit
                </Button>
              ) : (
                <Button
                  colorScheme="green"
                  flex="1"
                  borderRadius={"unset"}
                  p={2}
                  onClick={async () => {
                    // Combine country code and phone number
                    const fullPhoneNumber = whatsappPhone
                      ? whatsappCountryCode + whatsappPhone
                      : "";

                    // Update user data
                    await changeUserData({
                      first_name: firstName,
                      last_name: lastName,
                      whatsapp_phone: fullPhoneNumber,
                      whatsapp_opt_in: whatsappOptIn,
                    });

                    // Update organization if admin and organization changed
                    if (
                      isAdmin &&
                      selectedOrganizationId &&
                      typeof selectedOrganizationId === "number" &&
                      selectedOrganizationId !== user.organization
                    ) {
                      await changeUserOrganization(selectedOrganizationId);
                    }

                    setFieldStatus(false);
                  }}
                  isLoading={loading}
                >
                  Apply
                </Button>
              )}
              <Box w={4} />
              <Button
                colorScheme="gray"
                borderRadius={"unset"}
                onClick={logoutUser}
                flex="1"
                fontStyle="semibold"
              >
                Logout
              </Button>
            </Flex>
          </VStack>

          <Flex
            justifyItems="space-between"
            alignItems="center"
            mb={4}
            fontStyle="italic"
            fontWeight="light"
            m={3}
          >
            <Text>Date joined:</Text>
            <Text>
              {_date_joined.toLocaleDateString("en-US", {
                year: "numeric",
                month: "long",
                day: "numeric",
              })}
            </Text>
          </Flex>
        </Container>

        <Container
          p={4}
          bg={useColorModeValue("white", "gray.900")}
          boxShadow="lg"
          borderRadius="md"
          mt={4}
          maxW="xl"
        >
          <Text fontWeight="bold" fontSize="lg" mb={2}>
            <Trans>Password</Trans>
          </Text>
          <Text fontSize="sm" mb={4} color={useColorModeValue("gray.600", "gray.300")}>
            <Trans>You can change your password at any time by clicking the button below.</Trans>
          </Text>
          <Button
            fontSize="md"
            onClick={() => navigate("/profile/password-change")}
            variant="outline"
            size="sm"
            colorScheme="dark"
            borderRadius={"unset"}
            p={2}
            flexShrink={0}
            fontStyle="semibold"
            _hover={{ bg: "blackAlpha.100" }}
          >
            <Trans>Change Password</Trans>
          </Button>
        </Container>

        <InviteUserForm />
      </Skeleton>
    </Navbar>
  );
};

export default ProfileView;
